import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

type SnackbarContextValue = {
  showSnackbar: (message: string) => void;
};

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

const DISMISS_MS = 4500;

export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error("useSnackbar must be used within SnackbarProvider");
  }
  return ctx;
}

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setMessage(null);
  }, []);

  const showSnackbar = useCallback(
    (msg: string) => {
      const trimmed = msg.trim();
      if (!trimmed) return;

      if (timerRef.current) clearTimeout(timerRef.current);
      setMessage(trimmed);
      timerRef.current = setTimeout(dismiss, DISMISS_MS);
    },
    [dismiss]
  );

  useEffect(() => () => dismiss(), [dismiss]);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <div
        className={`poca-snackbar${message ? " is-visible" : ""}`}
        role="status"
        aria-live="polite"
        aria-hidden={!message}
      >
        <p className="poca-snackbar__text">{message}</p>
        <button
          type="button"
          className="poca-snackbar__close"
          aria-label="Dismiss"
          onClick={dismiss}
          tabIndex={message ? 0 : -1}
        >
          ×
        </button>
      </div>
    </SnackbarContext.Provider>
  );
}
